/* ENOCH ONE: local search of public content. No model, analytics, or query storage. */
(() => {
  'use strict';

  const boot = () => {
    const root = document.getElementById('ew-ask');
    if (!root || root.dataset.ready === 'true') return;
    const dialog = document.getElementById('ew-ask-dialog');
    const form = document.getElementById('ew-ask-form');
    const input = document.getElementById('ew-ask-input');
    const refineForm = document.getElementById('ew-ask-refine-form');
    const refine = document.getElementById('ew-ask-refine');
    const results = document.getElementById('ew-ask-results');
    const status = document.getElementById('ew-ask-status');
    const close = document.getElementById('ew-ask-close');
    if (!dialog || typeof dialog.showModal !== 'function' || !form || !input ||
        !refineForm || !refine || !results || !status || !close) return;

    const parse = (value, fallback) => {
      try { return JSON.parse(value); } catch { return fallback; }
    };
    const rawTranslations = parse(document.getElementById('ew-ask-i18n')?.textContent || '{}', {});
    const translations = rawTranslations && typeof rawTranslations === 'object' && !Array.isArray(rawTranslations)
      ? Object.fromEntries(Object.entries(rawTranslations).filter(([, value]) => typeof value === 'string')) : {};
    const required = ['loading', 'empty_question', 'no_answer', 'no_pages', 'faq_heading',
      'pages_heading', 'sources_heading', 'reviewed_label', 'catalog_unavailable',
      'result_summary', 'faq_tag', 'page_tag', 'query_changed'];
    if (required.some(key => !translations[key])) return;
    const t = key => translations[key] || '';

    // Only configured content URLs are fetched. Search text never enters a request URL.
    const safeURL = (value, internalOnly = false) => {
      if (typeof value !== 'string' || !value.trim()) return null;
      try {
        const url = new URL(value, location.href);
        if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password) return null;
        if (internalOnly && url.origin !== location.origin) return null;
        if (url.origin !== location.origin && url.protocol !== 'https:') return null;
        return url;
      } catch { return null; }
    };
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    const decorate = value => {
      const url = safeURL(value);
      if (!url) return null;
      if (preview && /^\d+$/.test(preview) && url.origin === location.origin) {
        url.searchParams.set('preview_theme_id', preview);
      }
      return url.href;
    };
    const normalize = value => String(value).normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const stopWords = new Set(('a an and are as at be by can could did do does for from give had has have ' +
      'how i in into is it its me my of on or our please should show tell that the their them there these ' +
      'they this to us was we were what when where which who why will with would you your about find ' +
      'information explain more want').split(' '));
    const words = value => normalize(value).split(' ').filter(Boolean);
    const queryWords = value => [...new Set(words(value).filter(word => word.length > 1 && !stopWords.has(word)))].slice(0, 24);
    const wordMatch = (term, candidate) => candidate === term ||
      (term.length >= 4 && candidate.startsWith(term)) ||
      (term.length >= 4 && term.endsWith('s') && candidate === term.slice(0, -1));
    const hit = (term, haystack) => haystack.some(candidate => wordMatch(term, candidate));
    const buildIndex = (primary, secondary) => ({ primary: words(primary), secondary: words(secondary) });
    const rank = (index, terms) => {
      let matched = 0;
      let score = 0;
      terms.forEach(term => {
        if (hit(term, index.primary)) { matched += 1; score += 5; }
        else if (hit(term, index.secondary)) { matched += 1; score += 1; }
      });
      return { coverage: matched / terms.length, score };
    };
    const rawDirectory = parse(document.getElementById('ew-directory-data')?.textContent || '[]', []);
    const seenPages = new Set();
    const directory = (Array.isArray(rawDirectory) ? rawDirectory : []).filter(entry => {
      if (!entry || typeof entry !== 'object' ||
          !['title', 'category', 'description'].every(key => typeof entry[key] === 'string')) return false;
      const url = safeURL(entry.url, true);
      if (!url || seenPages.has(url.href)) return false;
      seenPages.add(url.href);
      return true;
    }).map(entry => ({
      ...entry,
      url: decorate(entry.url),
      index: buildIndex(`${entry.title} ${entry.category}`, [entry.description, typeof entry.keywords === 'string' ? entry.keywords : ''].join(' '))
    }));

    let catalogPromise;
    let searchVersion = 0;
    let opener = input;
    const loadCatalog = () => {
      if (catalogPromise) return catalogPromise;
      catalogPromise = (async () => {
        const url = safeURL(root.dataset.catalogUrl);
        if (!url || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com' &&
            !url.hostname.endsWith('.myshopify.com'))) throw new Error('Invalid catalog origin');
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(url.href, {
            method: 'GET', credentials: 'omit', referrerPolicy: 'no-referrer', signal: controller.signal
          });
          if (!response.ok) throw new Error('Catalog unavailable');
          const catalog = await response.json();
          if (catalog?.publicAccess !== true || !Array.isArray(catalog.records)) throw new Error('Catalog is not public');
          return catalog.records.filter(record => record && record.classification === 'public' &&
            typeof record.question === 'string' && typeof record.answer === 'string' &&
            Array.isArray(record.references)).map(record => ({
              question: record.question,
              answer: record.answer,
              reviewed: typeof record.reviewed === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(record.reviewed) ? record.reviewed : '',
              references: record.references.filter(ref => ref && typeof ref.label === 'string' && safeURL(ref.url))
                .map(ref => ({ label: ref.label, url: decorate(ref.url) })),
              index: buildIndex(`${record.question} ${typeof record.keywords === 'string' ? record.keywords : ''}`, record.answer)
            })).filter(record => record.references.length > 0);
        } finally { clearTimeout(timeout); }
      })().catch(error => { catalogPromise = null; throw error; });
      return catalogPromise;
    };

    const element = (tag, className, value) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (value !== undefined) node.textContent = value;
      return node;
    };
    const link = (label, href) => {
      const node = element('a', '', label);
      node.href = href;
      return node;
    };
    const renderFAQ = record => {
      const card = element('article', 'ew-ask-card');
      const meta = record.reviewed ? `${t('faq_tag')} · ${t('reviewed_label')} ${record.reviewed}` : t('faq_tag');
      card.append(element('p', 'ew-ask-meta', meta), element('h4', '', record.question), element('p', '', record.answer));
      card.append(element('p', 'ew-ask-source-label', t('sources_heading')));
      const sources = element('ul', 'ew-ask-sources');
      record.references.forEach(ref => {
        const item = element('li');
        item.append(link(ref.label, ref.url));
        sources.append(item);
      });
      card.append(sources);
      return card;
    };
    const renderPage = entry => {
      const card = element('article', 'ew-ask-card');
      const imageURL = safeURL(entry.image);
      if (imageURL && (imageURL.origin === location.origin || imageURL.hostname === 'cdn.shopify.com')) {
        const logo = element('img', 'ew-ask-card-image');
        logo.src = imageURL.href;
        logo.alt = typeof entry.imageAlt === 'string' ? entry.imageAlt : entry.title;
        logo.width = 466;
        logo.height = 180;
        logo.loading = 'lazy';
        logo.decoding = 'async';
        card.append(logo);
      }
      card.append(element('p', 'ew-ask-meta', `${t('page_tag')} · ${entry.category}`));
      const title = element('h4');
      title.append(link(entry.title, entry.url));
      card.append(title, element('p', '', entry.description));
      return card;
    };

    const runSearch = async value => {
      const version = ++searchVersion;
      const question = value.slice(0, 240).trim();
      input.value = question;
      refine.value = question;
      results.replaceChildren();
      results.setAttribute('aria-busy', 'false');
      if (!dialog.open) {
        opener = document.activeElement;
        dialog.showModal();
        refine.focus();
      }
      const terms = queryWords(question);
      if (!terms.length) { status.textContent = t('empty_question'); return; }
      status.textContent = t('loading');
      results.setAttribute('aria-busy', 'true');
      const pages = directory.map(entry => ({ entry, ...rank(entry.index, terms) }))
        .filter(match => match.score > 0 && match.coverage >= .3)
        .sort((a, b) => b.coverage - a.coverage || b.score - a.score ||
          (Number.isFinite(a.entry.rank) ? a.entry.rank : 100) - (Number.isFinite(b.entry.rank) ? b.entry.rank : 100))
        .slice(0, 6).map(match => match.entry);
      let catalog = [];
      let unavailable = false;
      try { catalog = await loadCatalog(); } catch { unavailable = true; }
      // An older async result must never overwrite a new query or a closed dialog.
      if (version !== searchVersion || !dialog.open) return;
      const answers = catalog.map(record => ({ record, ...rank(record.index, terms) }))
        .filter(match => match.score >= 5 && match.coverage >= .45)
        .sort((a, b) => b.coverage - a.coverage || b.score - a.score)
        .slice(0, 4).map(match => match.record);
      results.setAttribute('aria-busy', 'false');
      status.textContent = t('result_summary').replace('{answers}', String(answers.length)).replace('{pages}', String(pages.length));
      if (unavailable) results.append(element('p', 'ew-ask-empty', t('catalog_unavailable')));
      else if (!answers.length) results.append(element('p', 'ew-ask-empty', t('no_answer')));
      if (answers.length) {
        results.append(element('h3', '', t('faq_heading')));
        answers.forEach(record => results.append(renderFAQ(record)));
      }
      if (pages.length) {
        results.append(element('h3', '', t('pages_heading')));
        pages.forEach(entry => results.append(renderPage(entry)));
      } else results.append(element('p', 'ew-ask-empty', t('no_pages')));
    };

    form.addEventListener('submit', event => { event.preventDefault(); void runSearch(input.value); });
    refineForm.addEventListener('submit', event => { event.preventDefault(); void runSearch(refine.value); });
    refine.addEventListener('input', () => {
      searchVersion += 1;
      results.replaceChildren();
      results.setAttribute('aria-busy', 'false');
      status.textContent = t('query_changed');
    });
    close.addEventListener('click', () => dialog.close());
    dialog.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        dialog.close();
      }
    });
    dialog.addEventListener('close', () => {
      searchVersion += 1;
      results.setAttribute('aria-busy', 'false');
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    document.querySelectorAll('[data-ew-ask-open]').forEach(control => {
      control.addEventListener('click', event => {
        event.preventDefault();
        input.focus();
        input.scrollIntoView({ block: 'center', behavior: 'instant' });
      });
    });
    root.dataset.ready = 'true';
    form.hidden = false;
    root.classList.add('is-ready');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();

